import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CircleCheck, CreditCard, ExternalLink, Gift, Sparkles, TriangleAlert } from 'lucide-react';
import { useEffect, useState, type ReactNode } from 'react';
import { useSearchParams } from 'react-router';
import { PaymentActions, paymentIssueText } from '../components/plan';
import { useToast } from '../components/toast';
import { Alert, Badge, Button, Card, CardHeader, cn, ConfirmDialog, ErrorState, PageHeader, PageLoader } from '../components/ui';
import { api, errorMessage } from '../lib/api';
import { useAuth } from '../lib/auth';
import {
  bandNote,
  FEATURE_LABEL,
  formatPrice,
  needsQuote,
  paying,
  SUBSCRIPTION_STATUS_LABEL,
  type BillingFeature,
  type BillingOverview,
  type CatalogItem,
  type PaymentIssue,
} from '../lib/billing';
import { formatDate } from '../lib/format';

type Change = { item: CatalogItem & { key: BillingFeature }; on: boolean };

/** What happens when an add-on is added or removed, in the confirmation. */
function changeText({ item, on }: Change, billing: BillingOverview) {
  const price = formatPrice(item);
  if (item.interval === 'year') {
    const held = billing.annualAddOns.find((addOn) => addOn.feature === item.key);
    const band = item.tier?.upTo ? ` It covers up to ${item.tier.upTo.toLocaleString()} Chromebooks.` : '';
    return on
      ? `Your card on file is charged ${price.replace('/year', '')} now, and again each year until you remove it.${band}`
      : `It stays on until ${formatDate(held?.periodEnd)}, the end of the year you paid for, and isn’t renewed.`;
  }
  if (on) return `It’s added to your subscription now at ${price}. This month is charged for the days that are left.`;
  const effects: Partial<Record<BillingFeature, string>> = {
    FACILITIES: 'Facilities pages are hidden until it’s added back. Nothing is deleted.',
    GOOGLE_PROVISIONING: 'Google Workspace Provisioning stops syncing. Accounts it made stay in Google.',
    FREQUENT_SYNC: 'Integrations that sync every hour or every 6 hours move to daily syncs.',
  };
  return `It’s removed now, and your next invoice is credited for the unused time. ${effects[item.key] ?? ''}`;
}

/** A school's Edvora plan: what it includes, subscribing, add-ons, and Stripe's portal for cards and invoices. */
export function BillingPage() {
  const toast = useToast();
  const queryClient = useQueryClient();
  const { me } = useAuth();
  const [params, setParams] = useSearchParams();
  const returned = params.get('checkout');
  const [chosen, setChosen] = useState<BillingFeature[]>([]);
  const [change, setChange] = useState<Change | null>(null);

  const billing = useQuery({
    queryKey: ['billing'],
    queryFn: () => api.get<BillingOverview>('/billing'),
    // Back from Checkout, the subscription shows up once Stripe's webhook arrives.
    refetchInterval: (query) => (returned === 'done' && !paying(query.state.data?.subscription?.status) ? 2000 : false),
  });
  const data = billing.data;
  const subscribed = paying(data?.subscription?.status);

  useEffect(() => {
    if (returned === 'done' && subscribed) {
      toast.success('Thanks! Your subscription is active.');
      void queryClient.invalidateQueries({ queryKey: ['me'] });
      setParams({}, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [returned, subscribed]);

  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: ['billing'] });
    void queryClient.invalidateQueries({ queryKey: ['me'] });
    void queryClient.invalidateQueries({ queryKey: ['integrations'] });
  };

  const checkout = useMutation({
    mutationFn: () => api.post<{ url: string }>('/billing/checkout', { addOns: chosen }),
    onSuccess: ({ url }) => window.location.assign(url),
    onError: (err) => toast.error(errorMessage(err)),
  });
  const portal = useMutation({
    mutationFn: () => api.post<{ url: string }>('/billing/portal'),
    onSuccess: ({ url }) => window.location.assign(url),
    onError: (err) => toast.error(errorMessage(err)),
  });
  const setAddOn = useMutation({
    mutationFn: ({ item, on }: Change) => api.put<{ billing: BillingOverview; url: string | null }>(`/billing/add-ons/${item.key}`, { on }),
    onSuccess: ({ url }, { item, on }) => {
      setChange(null);
      if (url) {
        window.location.assign(url);
        return;
      }
      refresh();
      toast.success(on ? `Added ${item.name}` : `Removed ${item.name}`);
    },
    onError: (err) => {
      setChange(null);
      toast.error(errorMessage(err));
    },
  });

  if (billing.isLoading) return <PageLoader />;
  if (billing.error || !data) return <ErrorState error={billing.error} onRetry={() => billing.refetch()} />;

  const platform = data.catalog.find((item) => item.key === 'PLATFORM')!;
  const addOns = data.catalog.filter((item): item is CatalogItem & { key: BillingFeature } => item.key !== 'PLATFORM');
  const monthly = (keys: BillingFeature[]) =>
    platform.amount + addOns.filter((item) => item.interval === 'month' && keys.includes(item.key)).reduce((sum, item) => sum + item.amount, 0);
  const annualHeld = (key: BillingFeature) => data.annualAddOns.find((addOn) => addOn.feature === key && paying(addOn.status));
  // An unpaid subscription is renewed by paying its invoice.
  const unpaid = data.subscription?.status === 'UNPAID';
  const canCheckout = data.configured && !subscribed && !unpaid;

  return (
    <>
      <PageHeader
        title="Plan & billing"
        description="Your school’s Edvora subscription: the platform and the add-ons you use. Payments are handled securely by Stripe."
        actions={
          data.hasCustomer && (
            <Button icon={<ExternalLink />} loading={portal.isPending} onClick={() => portal.mutate()}>
              Payment methods & invoices
            </Button>
          )
        }
      />

      <div className="space-y-6">
        {returned === 'done' && !subscribed && (
          <Alert tone="blue" title="Finishing up with Stripe…">
            Your subscription shows here as soon as Stripe confirms the payment.
          </Alert>
        )}
        {returned === 'error' && (
          <Alert tone="amber" title="Stripe didn’t open">
            Your school is ready, but the payment page couldn’t start. Choose Subscribe to try again, or email{' '}
            <a href={`mailto:${data.salesEmail}`} className="font-medium underline">
              {data.salesEmail}
            </a>
            .
          </Alert>
        )}
        {data.deletesAt && !subscribed && (
          <Alert tone={data.basis === 'TRIAL' ? 'amber' : 'red'} icon={<TriangleAlert />} title={data.basis === 'TRIAL' ? `Your free trial ends on ${formatDate(data.trialEndsAt)}` : `Your school will be deleted on ${formatDate(data.deletesAt)}`}>
            {data.basis === 'TRIAL'
              ? `Subscribe to keep using Edvora. If you don’t choose and pay for a plan, your school’s account and all of its data are deleted ${data.retentionDays} days after the trial, on ${formatDate(data.deletesAt)}.`
              : 'Subscribe before then to keep everything. After that date, your school’s account and all of its data are permanently deleted.'}{' '}
            Need more time? Email{' '}
            <a href={`mailto:${data.salesEmail}`} className="font-medium underline">
              {data.salesEmail}
            </a>{' '}
            to ask for an extension.
          </Alert>
        )}
        {returned === 'canceled' && (
          <Alert tone="amber" title="Checkout was canceled">
            Nothing was charged.{' '}
            <button className="font-medium underline" onClick={() => setParams({}, { replace: true })}>
              Dismiss
            </button>
          </Alert>
        )}
        {!data.configured && (
          <Alert tone={data.enforced ? 'amber' : 'blue'} title="Subscriptions aren’t set up on this Edvora environment">
            {data.enforced ? 'Contact Edvora to renew your plan.' : 'Everything in Edvora is included for now.'}
          </Alert>
        )}
        {data.paymentIssues.map((issue) => {
          const text = paymentIssueText(issue, me?.tenant?.name ?? 'Your school');
          return (
            <Alert key={issue.item} tone="red" icon={<TriangleAlert />} title={text.title}>
              <p>
                {text.body} {nextStep(issue)}
              </p>
              {issue.invoiceUrl && (
                <div className="mt-3">
                  <PaymentActions issue={issue} compact onBillingPage />
                </div>
              )}
            </Alert>
          );
        })}

        <Card>
          <CardHeader title="Your plan" />
          <div className="flex flex-wrap items-center gap-x-8 gap-y-3 p-5 text-sm">
            <PlanStatus billing={data} />
          </div>
        </Card>

        <Card>
          <CardHeader
            title="Edvora platform and add-ons"
            description={
              subscribed
                ? 'Add or remove add-ons any time. Changes are prorated.'
                : unpaid
                  ? 'Pay the overdue invoice to renew your subscription. You can change add-ons after that.'
                  : 'Choose the add-ons you’d like, then subscribe.'
            }
          />
          <ul className="divide-y divide-slate-100">
            <li className="flex flex-wrap items-start gap-4 px-5 py-4">
              <div className="min-w-0 flex-1">
                <p className="font-medium text-slate-900">{platform.name}</p>
                <p className="text-sm text-slate-500">{platform.description}</p>
              </div>
              <p className="w-32 text-right font-medium whitespace-nowrap text-slate-900">{formatPrice(platform)}</p>
              <div className="flex w-40 justify-end">{subscribed ? <Badge tone="green">Included</Badge> : <Badge>Required</Badge>}</div>
            </li>
            {addOns.map((item) => {
              const granted = data.grantedAddOns.includes(item.key);
              const held = item.interval === 'year' ? annualHeld(item.key) : undefined;
              const paid = item.interval === 'year' ? !!held : !!data.subscription?.addOns.includes(item.key) && subscribed;
              const ending = !!held?.cancelAtPeriodEnd;
              const quote = needsQuote(item);
              const band = bandNote(item);
              return (
                <li key={item.key} className="flex flex-wrap items-start gap-4 px-5 py-4">
                  <div className="min-w-0 flex-1">
                    <p className="font-medium text-slate-900">{item.name}</p>
                    <p className="text-sm text-slate-500">{item.description}</p>
                    {band && <p className="mt-1 text-xs text-slate-500">{band}</p>}
                    {item.interval === 'year' && !subscribed && <p className="mt-1 text-xs text-slate-500">Billed yearly. Add it once you’ve subscribed.</p>}
                  </div>
                  <p className="w-32 text-right font-medium whitespace-nowrap text-slate-900">{quote && !paid ? 'By quote' : formatPrice(item)}</p>
                  <div className="flex w-40 flex-col items-end gap-1.5">
                    {granted ? (
                      <Badge tone="violet">
                        <Gift className="size-3" /> Free from Edvora
                      </Badge>
                    ) : subscribed ? (
                      <>
                        {paid && <Badge tone={ending ? 'amber' : 'green'}>{ending ? `Ends ${formatDate(held?.periodEnd)}` : 'Added'}</Badge>}
                        {quote && !paid ? (
                          <a className="text-sm font-medium text-brand-700 hover:underline" href={`mailto:${data.salesEmail}?subject=${encodeURIComponent(item.name)}`}>
                            Ask Edvora for a quote
                          </a>
                        ) : (
                          <Button
                            size="sm"
                            variant={paid && !ending ? 'ghost' : 'secondary'}
                            disabled={!data.configured}
                            onClick={() => setChange({ item, on: !paid || !!ending })}
                          >
                            {ending ? 'Keep it' : paid ? 'Remove' : 'Add'}
                          </Button>
                        )}
                      </>
                    ) : item.interval === 'month' ? (
                      <label className="inline-flex cursor-pointer items-center gap-2 text-sm text-slate-700">
                        <input
                          type="checkbox"
                          className="size-4 rounded border-slate-300 accent-brand-700"
                          checked={chosen.includes(item.key)}
                          disabled={!canCheckout}
                          onChange={(event) => setChosen((keys) => (event.target.checked ? [...keys, item.key] : keys.filter((key) => key !== item.key)))}
                        />
                        Include
                      </label>
                    ) : null}
                  </div>
                </li>
              );
            })}
          </ul>
          <div className="flex flex-wrap items-center justify-between gap-3 border-t border-slate-100 bg-slate-50/60 px-5 py-4">
            <p className="text-sm text-slate-600">
              {subscribed ? 'Your subscription' : 'Total'}:{' '}
              <strong className="text-slate-900">
                {formatPrice({ ...platform, amount: monthly(subscribed ? (data.subscription?.addOns ?? []) : chosen) })}
              </strong>
              {subscribed &&
                data.annualAddOns
                  .filter((addOn) => paying(addOn.status))
                  .map((addOn) => addOns.find((item) => item.key === addOn.feature))
                  .filter((item): item is CatalogItem & { key: BillingFeature } => !!item)
                  .map((item) => (
                    <span key={item.key}>
                      {' '}
                      + <strong className="text-slate-900">{formatPrice(item)}</strong>
                    </span>
                  ))}
            </p>
            {canCheckout && (
              <Button variant="primary" icon={<CreditCard />} loading={checkout.isPending} onClick={() => checkout.mutate()}>
                Subscribe
              </Button>
            )}
          </div>
        </Card>
      </div>

      {change && (
        <ConfirmDialog
          open
          onClose={() => setChange(null)}
          onConfirm={() => setAddOn.mutate(change)}
          loading={setAddOn.isPending}
          tone={change.on ? 'primary' : 'danger'}
          title={change.on ? `Add ${change.item.name}?` : `Remove ${change.item.name}?`}
          description={changeText(change, data)}
          confirmLabel={change.on ? `Add for ${formatPrice(change.item)}` : 'Remove'}
        />
      )}
    </>
  );
}

/** How to settle an unpaid renewal from this page. */
function nextStep(issue: PaymentIssue) {
  if (issue.status === 'CANCELED') return issue.item === 'PLATFORM' ? 'Choose your add-ons below and subscribe.' : 'Add it again below.';
  if (issue.status === 'PAST_DUE' && !issue.lapsed) {
    return 'Stripe also tries your card again over the next few days, so updating it under Payment methods & invoices is enough.';
  }
  return issue.invoiceUrl ? '' : 'Pay it under Payment methods & invoices.';
}

function PlanStatus({ billing }: { billing: BillingOverview }) {
  const fact = (label: string, value: ReactNode, detail?: ReactNode) => (
    <div>
      <p className="text-xs font-medium text-slate-500">{label}</p>
      <p className="mt-0.5 font-medium text-slate-900">{value}</p>
      {detail && <p className="text-xs text-slate-500">{detail}</p>}
    </div>
  );
  const subscription = billing.subscription;
  const status = subscription?.status ?? null;

  return (
    <>
      {billing.basis === 'COMPLIMENTARY' &&
        fact(
          'Plan',
          <span className="inline-flex items-center gap-1.5">
            <Gift className="size-4 text-violet-600" /> Complimentary
          </span>,
          'Edvora provides this school’s plan, with every add-on, at no charge.',
        )}
      {billing.basis === 'TRIAL' &&
        fact(
          'Plan',
          <span className="inline-flex items-center gap-1.5">
            <Sparkles className="size-4 text-brand-600" /> Free trial
          </span>,
          `${billing.trialKind === 'SIGN_UP' ? 'Tech and Facilities are included, with manual roster and device syncs,' : 'Everything is included'} until ${formatDate(billing.trialEndsAt)}. ${paying(status) ? 'Your subscription is billed from then on.' : 'Subscribe to keep using Edvora after that.'}`,
        )}
      {billing.basis === 'UNMETERED' && fact('Plan', 'Everything included', 'Subscriptions aren’t required on this Edvora environment yet.')}
      {status &&
        fact(
          'Subscription',
          <span className="inline-flex items-center gap-1.5">
            {paying(status) && <CircleCheck className="size-4 text-emerald-600" />}
            <span className={cn(!paying(status) && 'text-red-700')}>{SUBSCRIPTION_STATUS_LABEL[status]}</span>
          </span>,
          paying(status) && subscription?.currentPeriodEnd
            ? `${subscription.cancelAtPeriodEnd ? 'Ends' : 'Renews'} ${formatDate(subscription.currentPeriodEnd)}`
            : !paying(status) && subscription?.currentPeriodEnd
              ? `Ended ${formatDate(subscription.currentPeriodEnd)}`
              : undefined,
        )}
      {billing.basis === 'NONE' && !status && fact('Subscription', <span className="text-red-700">Not subscribed</span>, 'Subscribe to use Edvora.')}
      {billing.enforced &&
        fact(
          'Add-ons in your plan',
          (Object.keys(billing.features) as BillingFeature[]).filter((feature) => billing.features[feature]).map((feature) => FEATURE_LABEL[feature]).join(', ') || 'None',
        )}
    </>
  );
}
